import { useEffect, useState } from "react";
import { fetchStudioApplications } from "./api/client";

const APPLICATION_REFRESH_INTERVAL_MS = 30_000;

export default function useStudioApplications(enabled = true) {
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(Boolean(enabled));
  const [applicationsError, setApplicationsError] = useState("");
  const [applicationsRefreshedAt, setApplicationsRefreshedAt] = useState(null);

  useEffect(() => {
    let active = true;
    let inFlight = false;

    if (!enabled) {
      setApplications([]);
      setApplicationsLoading(false);
      setApplicationsError("");
      setApplicationsRefreshedAt(null);
      return () => { active = false; };
    }

    const refresh = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await fetchStudioApplications();
        if (!Array.isArray(result)) throw new Error("Invalid application directory.");
        if (active) {
          setApplications(result.filter((item) => item && typeof item === "object" && !Array.isArray(item)));
          setApplicationsRefreshedAt(new Date());
          setApplicationsError("");
        }
      } catch {
        if (active) setApplicationsError("Application directory refresh failed; last-known entitlements are not treated as current after three intervals.");
      } finally {
        inFlight = false;
        if (active) setApplicationsLoading(false);
      }
    };

    setApplicationsLoading(true);
    refresh();
    const interval = window.setInterval(refresh, APPLICATION_REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [enabled]);

  const applicationsStale = !applicationsRefreshedAt
    || Date.now() - applicationsRefreshedAt.getTime() > APPLICATION_REFRESH_INTERVAL_MS * 3;

  return {
    applications,
    applicationsLoading,
    applicationsError,
    applicationsRefreshedAt,
    applicationsStale,
  };
}
